// gameActions.js
'use strict';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

import getOperationData from '../selector/getOperationData.js';
import getMeData from '../selector/getMeData.js';
import getModalData from '../selector/getModalData.js';

import setLiveStreamPanel from '../action/setLiveStreamPanel.js';
import setGameModalPosition from '../action/setGameModalPosition.js';
import mergeMeData from '../action/mergeMeData.js';
import toggleModal from '../action/toggleModal.js';
import { closeGameSicBo } from '../action/sicBoActions.js';

import {
  Game,
  GameHost,
  getUgppThirdPartyName,
} from '../resource/gameConstants.js';

const getIsValidGameHost = ({ host }) =>
  host === GameHost.LIVESTREAM_PANEL || host === GameHost.MODAL;

// Launch tokens are single use. Dropping them at the session transition keeps
// it off React unmount timing — the window can outlive the session it was
// opened for.
const clearLaunchToken =
  ({ gameId } = {}) =>
  async (dispatch, getState) => {
    const thirdPartyName = getUgppThirdPartyName({ gameId });
    const launchToken = getMeData(getState(), 'aquisitionAuthToken')?.[
      thirdPartyName
    ];
    if (!launchToken) {
      return;
    }
    return dispatch(
      mergeMeData({
        field: 'aquisitionAuthToken',
        value: { [thirdPartyName]: '' },
      })
    );
  };

/**
 * Open a game in the requested host and close the other host.
 * @kind action
 * @param {object} params - Parameters.
 * @param {string} params.gameId - The game ID to open.
 * @param {string} params.host - The host asking to render the game.
 * @param {string} [params.streamId] - The livestream to bind the game to.
 * @returns {Promise} Action promise.
 * @example
 * dispatch(openGame({
 *   gameId: 'ugpp-cupid-gift',
 *   host: GameHost.MODAL,
 *   streamId: 'stream-id',
 * }));
 */
const openGame =
  ({ gameId, host, streamId } = {}) =>
  async (dispatch, getState) => {
    if (!gameId || !getIsValidGameHost({ host })) {
      return;
    }

    // Cupid gift is a livestream gift; it has no meaning outside a room.
    if (Game.UGPP_CUPID_GIFT === gameId && !streamId) {
      return;
    }

    const activeGameId = getOperationData(getState(), ['game'], 'gameId');
    const activeGameHost = getOperationData(getState(), ['game'], 'host');
    if (
      activeGameId === Game.SIC_BO &&
      (activeGameId !== gameId || activeGameHost !== host)
    ) {
      dispatch(closeGameSicBo());
    }
    dispatch(clearLaunchToken({ gameId: activeGameId }));

    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['game'],
        data: { gameId, host, streamId },
      },
    });

    // Before the window mounts, so it opens at its anchor instead of inheriting
    // a spot dragged in another host.
    dispatch(setGameModalPosition({ position: null }));

    if (host === GameHost.LIVESTREAM_PANEL) {
      dispatch(setLiveStreamPanel({ isGameOpen: true }));
      // Closing a modal that is not open still books the shared animation
      // timer, and the late removal would take out a modal opened meanwhile.
      if (!getModalData(getState(), 'HomeGameModal', 'status')) {
        return;
      }
      return dispatch(toggleModal({ modalId: 'HomeGameModal', isOpen: false }));
    }

    dispatch(setLiveStreamPanel({ isGameOpen: false }));
    return dispatch(toggleModal({ modalId: 'HomeGameModal', isOpen: true }));
  };

/**
 * Close an active game when the requested game and host still match.
 * @kind action
 * @param {object} params - Parameters.
 * @param {string} params.gameId - The game ID expected to be active.
 * @param {string} params.host - The host expected to be active.
 * @returns {Promise} Action promise.
 * @example
 * dispatch(closeGame({
 *   gameId: 'ugpp-cupid-gift',
 *   host: GameHost.MODAL,
 * }));
 */
const closeGame =
  ({ gameId, host } = {}) =>
  async (dispatch, getState) => {
    if (!gameId || !getIsValidGameHost({ host })) {
      return;
    }

    const activeGameId = getOperationData(getState(), ['game'], 'gameId');
    const activeGameHost = getOperationData(getState(), ['game'], 'host');
    if (gameId !== activeGameId || host !== activeGameHost) {
      return;
    }

    // Field by field: the `game` slice also holds provider state.
    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['game'],
        data: { gameId: undefined, host: undefined, streamId: undefined },
      },
    });
    dispatch(setGameModalPosition({ position: null }));
    dispatch(clearLaunchToken({ gameId }));

    if (gameId === Game.SIC_BO) {
      dispatch(closeGameSicBo());
    }

    if (host === GameHost.LIVESTREAM_PANEL) {
      return dispatch(setLiveStreamPanel({ isGameOpen: false }));
    }

    return dispatch(toggleModal({ modalId: 'HomeGameModal', isOpen: false }));
  };

export { openGame, closeGame };
